import { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Button, Col, Row } from "antd";
import styled from "styled-components";
import Title from "../../../components/Title";
import { BranchesOutlined, DownloadOutlined } from "@ant-design/icons";
import { TYPE_GET_DATE } from "../../../../helper/const";
import {
  getDateChangeTime,
  formatDataTimer,
  exportExcel,
  formatDataNatAirtimeTable,
} from "../../../../helper/helper";
import FilterTime from "../../../components/FilterTime";
import _ from "lodash";
import DashboardAirtime from "../../components/DashboardAirtime";
import Tables from "../../../components/Tables";

export default function Overview({ handleGetData, tabActive }) {
  const [startDate, setStartDate] = useState(
    getDateChangeTime(TYPE_GET_DATE.oneMonthAgo)
  );
  const [endDate, setEndDate] = useState(getDateChangeTime(TYPE_GET_DATE.now));

  const { result = {} } =
    useSelector((state) => state.reducerNatcom?.data) || {};

  const {
    data: airtime,
    data: airtime_table,
    lastHourByDate,
  } = result;

  useEffect(() => {
    if (tabActive !== "1") return;
    handleGetData(startDate, endDate);
  }, [startDate, endDate, tabActive]);

  /**
   * =========================
   * 🔥 OVERVIEW TOTAL CHART
   * =========================
   * dùng lastHourByDate làm chuẩn
   */
  const columns = useMemo(() => {
    if (_.isEmpty(lastHourByDate) || tabActive !== "1") return [];

    const result = Object.values(lastHourByDate).map((item) => ({
      time: item.date,
      totalAdvanceFee: (Number(item.total_advance_fee) || 0) / 100,
      totalRecoveryOriginFee:
        (Number(item.total_recovery_origin_fee) || 0) / 100,
    }));

    const sorted = result.sort(
      (a, b) => new Date(a.time) - new Date(b.time)
    );

    return _.flatMap(sorted, (item) => [
      {
        time: item.time,
        totalData: item.totalAdvanceFee,
        name: "totalAdvanceFee",
      },
      {
        time: item.time,
        totalData: item.totalRecoveryOriginFee,
        name: "totalRecoveryOriginFee",
      },
    ]);
  }, [lastHourByDate, tabActive]);

  const dataDetail = {
    data: formatDataTimer(columns || []),
    xField: "time",
    yField: "totalData",
    seriesField: "name",
  };

  const width = columns.length * 25;

  const handleExportExcel = () => {
    exportExcel(airtime, "airtime_natcom");
  };

  /**
   * =========================
   * 🔥 OVERVIEW TABLE
   * =========================
   * dùng lastHourByDate luôn
   */
  let dataTableAirtime = [];

  if (!_.isEmpty(lastHourByDate)) {
    dataTableAirtime = Object.values(lastHourByDate).map((item) => ({
      ...item,
      total_advance_fee: Number(item.total_advance_fee) || 0,
      total_recovery_origin_fee:
        Number(item.total_recovery_origin_fee) || 0,
      total_advance: Number(item.total_advance) || 0,
      total_fee: Number(item.total_fee) || 0,
      basic_fee: Number(item.basic_fee) || 0,
      total_recovery_fee: Number(item.total_recovery_fee) || 0,
      total_trans_recovery: Number(item.total_trans_recovery) || 0,
      basic_advance_fee: Number(item.basic_advance_fee) || 0,
      basic_advance: Number(item.basic_advance) || 0,
      basic_recovery_origin_fee:
        Number(item.basic_recovery_origin_fee) || 0,
      basic_recovery_fee: Number(item.basic_recovery_fee) || 0,
      basic_trans_recovery:
        Number(item.basic_trans_recovery) || 0,
      date: item.date,
    }));
  } else {
    // fallback logic cũ nếu BE chưa trả lastHour
    for (const date in airtime_table) {
      const records = airtime_table[date] || [];

      const sumTotal = records.reduce(
        (acc, cur) => {
          acc.total_advance_fee += Number(cur.total_advance_fee) || 0;
          acc.total_recovery_origin_fee +=
            Number(cur.total_recovery_origin_fee) || 0;
          acc.total_advance += Number(cur.total_advance) || 0;
          acc.total_fee += Number(cur.total_fee) || 0;
          acc.basic_fee += Number(cur.basic_fee) || 0;
          acc.total_recovery_fee +=
            Number(cur.total_recovery_fee) || 0;
          acc.total_trans_recovery +=
            Number(cur.total_trans_recovery) || 0;
          acc.basic_advance_fee +=
            Number(cur.basic_advance_fee) || 0;
          acc.basic_advance += Number(cur.basic_advance) || 0;
          acc.basic_recovery_origin_fee +=
            Number(cur.basic_recovery_origin_fee) || 0;
          acc.basic_recovery_fee +=
            Number(cur.basic_recovery_fee) || 0;
          acc.basic_trans_recovery +=
            Number(cur.basic_trans_recovery) || 0;
          acc.date = cur.date;
          return acc;
        },
        {
          total_advance_fee: 0,
          total_recovery_origin_fee: 0,
          total_advance: 0,
          total_fee: 0,
          basic_fee: 0,
          total_recovery_fee: 0,
          total_trans_recovery: 0,
          basic_advance_fee: 0,
          basic_advance: 0,
          basic_recovery_origin_fee: 0,
          basic_recovery_fee: 0,
          basic_trans_recovery: 0,
          date: date,
        }
      );

      dataTableAirtime.push(sumTotal);
    }
  }

  // sort mới → cũ
  dataTableAirtime = dataTableAirtime.sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );

  const columnTable = useMemo(() => {
    if (!dataTableAirtime) return [];
    return formatDataNatAirtimeTable(dataTableAirtime);
  }, [dataTableAirtime]);

  return (
    <>
      <div
        className="position-relative d-flex justify-right mb-2 gap-2"
        style={{ justifyContent: "right" }}
      >
        <FilterTime
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
        <div className="d-flex justify-content-end">
          <Button
            onClick={handleExportExcel}
            className="d-flex align-items-center"
          >
            <DownloadOutlined />
            Export Excel
          </Button>
        </div>
      </div>

      <Container className="panel panel-sortable" width={width}>
        <Title titleName="Overview">
          <BranchesOutlined />
        </Title>

        <Col span={24}>
          <Col xs={24} lg={24} className="container-item">
            <h4 className="pt-3 pe-3 pb-5">
              Overview total chart:
            </h4>
            <div className="item-chart">
              <DashboardAirtime
                dataDashboard={dataDetail}
                isHiddenContainer
                width={1100}
              />
            </div>
          </Col>
        </Col>
      </Container>

      <Tables
        className="py-2"
        columns={columnTable}
        title="Overview statistic table"
        isHiddenPagination={false}
      />
    </>
  );
}

const Container = styled(Row)`
  width: 100%;
  overflow-x: auto;
  margin-bottom: 20px;

  .item-chart {
    min-width: ${(props) => `${props.width}px`};
    overflow-x: auto;
  }
`;
